import { OverlayView } from "@react-google-maps/api";
import { Paper, Typography, Box, ClickAwayListener } from "@mui/material";

// Format a date string as DD/MM/YY HH:MM
const formatDate = (dateStr: string): string => {
    if (!dateStr) return ''
    try {
        const d = new Date(dateStr)
        if (isNaN(d.getTime())) return dateStr
        const dd = String(d.getDate()).padStart(2, '0')
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const yy = String(d.getFullYear()).slice(-2)
        const hh = String(d.getHours()).padStart(2, '0')
        const min = String(d.getMinutes()).padStart(2, '0')
        return `${dd}/${mm}/${yy} ${hh}:${min}`
    } catch {
        return dateStr
    }
}

type MarkerInfoCardProps = {
    position: google.maps.LatLngLiteral;
    meterId: string;
    address: string;
    name: string;
    lastSeen: string;
    onClose: () => void;
    isDarkMode?: boolean;
};

export default function MarkerInfoCard({
    position,
    meterId,
    address,
    name,
    lastSeen,
    onClose,
    isDarkMode = false,
}: MarkerInfoCardProps) {
    const bgColor = isDarkMode ? '#1D325A' : '#F0F4FA';
    const borderColor = isDarkMode ? '#374151' : '#E0E0E0';
    const labelColor = isDarkMode ? '#8BB8E8' : '#003F61';
    const valueColor = isDarkMode ? '#FFFFFF' : '#2D3748';

    const rows = [
        { label: 'מספר מד', value: meterId },
        { label: 'כתובת', value: address },
        { label: 'שם', value: name },
        { label: 'ת.קליטה', value: lastSeen ? formatDate(lastSeen) : '' },
    ].filter(({ value }) => value != null && String(value).trim() !== '');

    return (
        <OverlayView position={position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
            <ClickAwayListener
                onClickAway={onClose}
                mouseEvent="onMouseDown"
                touchEvent="onTouchStart"
            >
                <Paper
                    elevation={4}
                    onMouseDown={e => e.stopPropagation()}
                    onTouchStart={e => e.stopPropagation()}
                    sx={{
                        backgroundColor: bgColor,
                        direction: 'rtl',
                        borderRadius: '10px',
                        border: `1px solid ${borderColor}`,
                        px: 1.8,
                        py: 1.2,
                        minWidth: 220,
                        maxWidth: 270,
                        boxSizing: 'border-box',
                        transition: 'background-color 0.3s ease, border-color 0.3s ease',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Box display="flex" flexDirection="column" gap={0.4}>
                        {rows.map(({ label, value }) => (
                            <Box key={label} display="flex" flexDirection="row" alignItems="flex-start" gap={1} sx={{ py: 0.1 }}>
                                <Typography sx={{
                                    fontFamily: 'Rubik, sans-serif',
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    color: labelColor,
                                    whiteSpace: 'nowrap',
                                    lineHeight: 1.4,
                                    width: 65,
                                    flexShrink: 0,
                                }}>
                                    {label}
                                </Typography>
                                <Typography sx={{
                                    fontFamily: 'Rubik, sans-serif',
                                    fontSize: '13px',
                                    fontWeight: 400,
                                    color: valueColor,
                                    lineHeight: 1.4,
                                    flex: 1,
                                    minWidth: 0,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    wordBreak: 'break-word',
                                }}>
                                    {value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Paper>
            </ClickAwayListener>
        </OverlayView>
    );
}
